import { Link } from "@tanstack/react-router";
import { QrCode, ScanLine, UsersRound } from "lucide-react";
import { useState } from "react";

import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";

import { MyCodeSheet } from "./MyCodeSheet";
import { raisedButtonClass } from "./button-styles";
import { ScanSheet } from "./ScanSheet";

interface ConnectEvent {
  eventId: string;
  slug: string;
  title: string;
}

function useConnectSheets() {
  const [open, setOpen] = useState<"code" | "scan" | null>(null);
  return {
    open,
    showCode: () => setOpen("code"),
    scan: () => setOpen("scan"),
    onOpenChange: (sheet: "code" | "scan") => (next: boolean) =>
      setOpen(next ? sheet : null),
  };
}

function Sheets({
  uid,
  event,
  sheets,
}: {
  uid: string;
  event: ConnectEvent;
  sheets: ReturnType<typeof useConnectSheets>;
}) {
  return (
    <>
      <MyCodeSheet
        uid={uid}
        eventSlug={event.slug}
        eventTitle={event.title}
        open={sheets.open === "code"}
        onOpenChange={sheets.onOpenChange("code")}
      />
      <ScanSheet
        uid={uid}
        eventId={event.eventId}
        open={sheets.open === "scan"}
        onOpenChange={sheets.onOpenChange("scan")}
      />
    </>
  );
}

/**
 * The Connect card (TNG-93), at the top of the Attendees tab, shown while the organiser has connections on:
 * show your code, or scan somebody else's.
 */
export function ConnectCard({
  uid,
  event,
}: {
  uid: string;
  event: ConnectEvent;
}) {
  const sheets = useConnectSheets();
  return (
    <Card
      className="border-lit-card-edge shadow-lit-card"
      data-testid="connect-card"
    >
      <CardContent className="space-y-3 py-4">
        <div className="flex items-start gap-3">
          <UsersRound
            className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <div className="min-w-0 space-y-1">
            <p className="font-semibold text-foreground">Connect</p>
            <p className="text-sm text-muted-foreground">
              Swap cards with someone by scanning their code. You both keep each
              other in{" "}
              <Link
                to="/connections"
                className="font-medium text-lit underline underline-offset-2"
              >
                your connections
              </Link>
              .
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            className={raisedButtonClass}
            onClick={sheets.showCode}
            data-testid="connect-show-code"
          >
            <QrCode />
            Show my code
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={sheets.scan}
            data-testid="connect-scan"
          >
            <ScanLine />
            Scan a code
          </Button>
        </div>
      </CardContent>
      <Sheets uid={uid} event={event} sheets={sheets} />
    </Card>
  );
}

/**
 * Connect on a person's profile sheet, while the event has connections on.
 * Connecting is always a code swap, so the sheet offers the same two actions
 * as the Connect card, or says the two of you are connected already.
 */
export function ConnectWithPerson({
  uid,
  event,
  personName,
  connected,
}: {
  uid: string;
  event: ConnectEvent;
  personName: string;
  connected: boolean;
}) {
  const sheets = useConnectSheets();
  if (connected) {
    return (
      <p
        className="flex items-center gap-2 text-sm text-foreground"
        data-testid="person-sheet-connected"
      >
        <UsersRound className="h-4 w-4 shrink-0" aria-hidden />
        <span>
          You are connected with {personName}. See{" "}
          <Link
            to="/connections"
            className="font-medium text-lit underline underline-offset-2"
          >
            your connections
          </Link>
          .
        </span>
      </p>
    );
  }
  return (
    <div className="space-y-2" data-testid="person-sheet-connect">
      <p className="text-sm text-muted-foreground">
        Swap codes with {personName} to keep each other in your connections.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          className={raisedButtonClass}
          onClick={sheets.showCode}
          data-testid="person-sheet-show-code"
        >
          <QrCode />
          Show my code
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={sheets.scan}
          data-testid="person-sheet-scan"
        >
          <ScanLine />
          Scan their code
        </Button>
      </div>
      <Sheets uid={uid} event={event} sheets={sheets} />
    </div>
  );
}
